import { DatePicker } from '../../../components/ui/date-picker';
// apps/web/src/pages/vendas/relatorio/relatorio-cliente-dialog.tsx — Fase 12, Plano 17 (PV-08, D-33..D-36).
// Diálogo "Relatório de acompanhamento ao cliente": o analista escolhe cliente, seções e formatos na hora.
// A geração roda em fila no servidor (POST /api/v1/vendas/relatorios responde com o registro PENDENTE); ao gerar,
// o diálogo fecha e um toast avisa. O acompanhamento e o download ficam no painel de relatórios recentes.
import { useEffect, useState } from 'react';
import {
  CODIGO_RELATORIO_PV_SEM_PV_ABERTO,
  type ClienteLookupPv,
  type FormatoRelatorioPv,
  type GerarRelatorioPvInput,
  type RelatorioPvView,
  type SecaoRelatorioPv,
} from '@pieltec/contracts';
import { ClienteCombobox } from '../../../components/vendas/cliente-combobox';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { FormMessage } from '../../../components/ui/form-message';
import { apiMutate } from '../../../lib/csrf-fetch';
import { lerErroApi } from '../../../lib/vendas/erro-api';
import { mensagemSemPvAberto, podeGerarRelatorio, rotuloEstadoRelatorio } from '../../../lib/vendas/relatorio';

interface RelatorioClienteDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  /** Cliente pré-preenchido (filtro da lista ou cliente do PV). */
  readonly clienteInicial?: ClienteLookupPv | null;
  /** Aberto pelo detalhe do PV: o cliente não pode ser trocado. */
  readonly clienteTravado?: boolean;
  /** Chamado com o registro criado (PENDENTE) para o painel passar a acompanhá-lo. */
  readonly onGerado: (relatorio: RelatorioPvView, cliente: ClienteLookupPv) => void;
}

const SECOES: readonly { readonly valor: SecaoRelatorioPv; readonly rotulo: string; readonly ajuda?: string }[] = [
  { valor: 'SALDOS', rotulo: 'Saldos por item' },
  { valor: 'ETAPA', rotulo: 'Etapa atual do lote', ajuda: 'Etapa atual mostra só o nome da etapa, sem célula ou cabine.' },
  { valor: 'PREVISAO', rotulo: 'Previsão de entrega' },
  { valor: 'PRECOS', rotulo: 'Preços e valores', ajuda: 'Contém valores comerciais. Confira antes de enviar ao cliente.' },
];

const FORMATOS: readonly { readonly valor: FormatoRelatorioPv; readonly rotulo: string }[] = [
  { valor: 'PDF', rotulo: 'PDF' },
  { valor: 'XLSX', rotulo: 'Excel' },
];

const TODAS_SECOES: readonly SecaoRelatorioPv[] = SECOES.map((s) => s.valor);
const TODOS_FORMATOS: readonly FormatoRelatorioPv[] = FORMATOS.map((f) => f.valor);

const MENSAGEM_FALHA_GERAR = 'Não foi possível gerar o relatório. Tente novamente.';

function alternar<T>(lista: readonly T[], item: T, marcado: boolean): T[] {
  const semItem = lista.filter((atual) => atual !== item);
  return marcado ? [...semItem, item] : semItem;
}

export function RelatorioClienteDialog({ aberto, onAbertoChange, clienteInicial = null, clienteTravado = false, onGerado }: RelatorioClienteDialogProps) {
  const [cliente, setCliente] = useState<ClienteLookupPv | null>(clienteInicial);
  const [secoes, setSecoes] = useState<readonly SecaoRelatorioPv[]>(TODAS_SECOES);
  const [formatos, setFormatos] = useState<readonly FormatoRelatorioPv[]>(TODOS_FORMATOS);
  const [incluirConcluidos, setIncluirConcluidos] = useState(false);
  const [periodoDe, setPeriodoDe] = useState('');
  const [periodoAte, setPeriodoAte] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Ao abrir: cliente pré-preenchido, seções e formatos todos marcados.
  useEffect(() => {
    if (!aberto) return;
    setCliente(clienteInicial);
    setSecoes(TODAS_SECOES);
    setFormatos(TODOS_FORMATOS);
    setIncluirConcluidos(false);
    setPeriodoDe('');
    setPeriodoAte('');
    setErro(null);
    // Reinicia só ao abrir: um `clienteInicial` recriado a cada render não pode descartar o que o usuário marcou.
  }, [aberto]);

  // Toast "Gerando relatório…" some sozinho em 5 s (padrão das demais telas de vendas).
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const habilitado = podeGerarRelatorio({ clienteId: cliente?.id ?? '', secoes, formatos, incluirConcluidos, periodoDe, periodoAte });

  async function gerar(): Promise<void> {
    if (!cliente || !habilitado) return;
    const corpo: GerarRelatorioPvInput = {
      clienteId: cliente.id,
      secoes,
      formatos,
      incluirConcluidos,
      ...(incluirConcluidos ? { periodoDe, periodoAte } : {}),
    };
    setErro(null);
    setEnviando(true);
    try {
      const res = await apiMutate('/api/v1/vendas/relatorios', { method: 'POST', body: corpo });
      if (!res.ok) {
        const erroApi = await lerErroApi(res, MENSAGEM_FALHA_GERAR);
        if (res.status === 409 && erroApi.codigo === CODIGO_RELATORIO_PV_SEM_PV_ABERTO) {
          // O diálogo permanece aberto: o analista pode marcar "Incluir concluídos no período".
          setErro(mensagemSemPvAberto(erroApi.mensagem, cliente.nome));
          return;
        }
        // 403 (sem permissão), PERIODO_INVALIDO e demais códigos chegam com o texto em português.
        setErro(erroApi.mensagem);
        return;
      }
      const relatorio = (await res.json()) as RelatorioPvView;
      onGerado(relatorio, cliente);
      onAbertoChange(false);
      setToast(rotuloEstadoRelatorio('GERANDO'));
    } catch {
      setErro(MENSAGEM_FALHA_GERAR);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <Dialog open={aberto} onOpenChange={onAbertoChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Relatório de acompanhamento ao cliente</DialogTitle>
            <DialogDescription>Escolha o cliente, as seções e os formatos do relatório.</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label htmlFor="relatorio-cliente" className="text-sm font-medium">
                Cliente *
              </label>
              <ClienteCombobox id="relatorio-cliente" value={cliente} onChange={setCliente} disabled={clienteTravado} />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="relatorio-incluir-concluidos" className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox id="relatorio-incluir-concluidos" checked={incluirConcluidos} onCheckedChange={(marcada) => setIncluirConcluidos(marcada === true)} />
                Incluir concluídos no período
              </label>
              {incluirConcluidos && (
                <div className="flex flex-wrap gap-3">
                  <div className="flex flex-col gap-1">
                    <label htmlFor="relatorio-periodo-de" className="text-xs font-medium text-slate-600">
                      De
                    </label>
                    <DatePicker id="relatorio-periodo-de" value={periodoDe} onValueChange={(value) => setPeriodoDe(value)} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label htmlFor="relatorio-periodo-ate" className="text-xs font-medium text-slate-600">
                      Até
                    </label>
                    <DatePicker id="relatorio-periodo-ate" value={periodoAte} onValueChange={(value) => setPeriodoAte(value)} />
                  </div>
                </div>
              )}
            </div>

            <fieldset className="flex flex-col gap-2">
              <legend className="text-sm font-medium">Seções do relatório</legend>
              {SECOES.map((secao) => {
                const idAjuda = `relatorio-secao-${secao.valor}-ajuda`;
                return (
                  <div key={secao.valor} className="flex flex-col gap-0.5">
                    <label htmlFor={`relatorio-secao-${secao.valor}`} className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox
                        id={`relatorio-secao-${secao.valor}`}
                        checked={secoes.includes(secao.valor)}
                        aria-describedby={secao.ajuda ? idAjuda : undefined}
                        onCheckedChange={(marcada) => setSecoes((atual) => alternar(atual, secao.valor, marcada === true))}
                      />
                      {secao.rotulo}
                    </label>
                    {secao.ajuda && (
                      <p id={idAjuda} className="pl-6 text-xs text-muted-foreground">
                        {secao.ajuda}
                      </p>
                    )}
                  </div>
                );
              })}
            </fieldset>

            <fieldset className="flex flex-col gap-2">
              <legend className="text-sm font-medium">Formato</legend>
              {FORMATOS.map((formato) => (
                <label key={formato.valor} htmlFor={`relatorio-formato-${formato.valor}`} className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    id={`relatorio-formato-${formato.valor}`}
                    checked={formatos.includes(formato.valor)}
                    onCheckedChange={(marcada) => setFormatos((atual) => alternar(atual, formato.valor, marcada === true))}
                  />
                  {formato.rotulo}
                </label>
              ))}
            </fieldset>

            {cliente && <p className="text-xs text-muted-foreground">Inclui todos os PVs em aberto de {cliente.nome}.</p>}

            {erro && <FormMessage>{erro}</FormMessage>}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onAbertoChange(false)}>
              Voltar
            </Button>
            <Button type="button" disabled={!habilitado || enviando} onClick={() => void gerar()}>
              Gerar relatório
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {toast && (
        <div
          role="status"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl bg-slate-900 px-4 py-3 text-white shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-150"
        >
          <span className="text-xs font-semibold">{toast}</span>
        </div>
      )}
    </>
  );
}
